'use client';

import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cx } from '../../utils/cx';
import styles from './TextField.module.css';

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  label?: string;
  description?: ReactNode;
  error?: ReactNode;
  size?: 'S' | 'M';
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  className?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      description,
      error,
      size = 'M',
      leadingIcon,
      trailingIcon,
      className,
      id,
      disabled,
      required,
      ...inputProps
    },
    ref
  ) => {
    const reactId = useId();
    const fieldId = id ?? reactId;
    const descriptionId = description ? `${fieldId}-description` : undefined;
    const errorId = error ? `${fieldId}-error` : undefined;
    const invalid = Boolean(error);

    return (
      <div
        className={cx(styles.root, className)}
        data-size={size}
        data-disabled={disabled || undefined}
        data-invalid={invalid || undefined}
      >
        {label && (
          <label className={styles.label} htmlFor={fieldId}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden={true}>
                *
              </span>
            )}
          </label>
        )}

        <div className={styles.inputWrap}>
          {leadingIcon && (
            <span className={styles.icon} data-side="leading">
              {leadingIcon}
            </span>
          )}
          <input
            {...inputProps}
            ref={ref}
            id={fieldId}
            disabled={disabled}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={cx(descriptionId, errorId) || undefined}
            className={styles.input}
          />
          {trailingIcon && (
            <span className={styles.icon} data-side="trailing">
              {trailingIcon}
            </span>
          )}
        </div>

        {description && !error && (
          <p className={styles.description} id={descriptionId}>
            {description}
          </p>
        )}
        {error && (
          <p className={styles.error} id={errorId}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';
